<template>
  <div class="property-form">
    <Form class="actions-property-form" ref="record" :model="record">
      <Row :gutter="20">
        <i-col
          v-for="(property, index) in properties"
          :key="property.name"
          :span="['list', 'textarea'].includes(property.control) ? '24' : '12'"
        >
          <FormItem
            :label="i18nLabel(property.label)"
            :prop="property.name"
            :show-message="false"
            :required="!property.optional && !isDetail && !(property.connection_related && isSelectConfiguration)"
          >
            <Tooltip max-width="250px" class="field-help" transfer placement="top" :content="i18nLabel(property.hint)">
              <Icon type="md-help-circle"> </Icon>
            </Tooltip>
            <!-- String -->
            <i-input
              v-if="property.control === 'text' && property.type !== 'int'"
              v-model="record[property.name]"
              :type="property.name === 'password' ? 'password' : 'text'"
              :password="property.name === 'password'"
              size="large"
              :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
              :placeholder="property.default ? property.default.toString() : ''"
            ></i-input>
            <!-- Textarea -->
            <i-input
              v-if="property.control === 'textarea' && property.type !== 'int'"
              v-model="record[property.name]"
              type="textarea"
              size="large"
              :rows="3"
              :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
              :placeholder="property.default ? property.default.toString() : ''"
            ></i-input>
            <!-- Number -->
            <i-input
              v-model.number="record[property.name]"
              v-if="property.control === 'text' && property.type === 'int'"
              type="number"
              size="large"
              :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
              :placeholder="property.default ? property.default.toString() : ''"
            ></i-input>
            <!-- Boolean -->
            <div class="radio-control" v-if="property.control === 'radio' && property.type === 'bool'">
              <span class="radio-item">
                <input
                  :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                  :id="`true-${index}-radio`"
                  type="radio"
                  v-model="record[property.name]"
                  :value="true"
                />
                <label :for="`true-${index}-radio`">True</label>
              </span>
              <span class="radio-item">
                <input
                  :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                  :id="`false-${index}-radio`"
                  type="radio"
                  v-model="record[property.name]"
                  :value="false"
                />
                <label :for="`false-${index}-radio`">False</label>
              </span>
            </div>
            <!-- Select -->
            <template v-if="property.control === 'select'">
              <!-- @on-change="selectChange($event, property.name)" -->
              <Select
                v-model="record[property.name]"
                size="large"
                :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                :placeholder="property.default ? property.default.toString() : ''"
                clearable
                @on-clear="handleClear(property.name)"
              >
                <Option v-for="(value, index) in property.values" :key="index" :value="value">{{ value }}</Option>
              </Select>
            </template>

            <!-- header -->
            <template v-if="property.control === 'list'">
              <!-- List -->
              <Select
                v-if="property.type !== 'list_object' && property.type !== 'object'"
                v-model="record[property.name]"
                filterable
                multiple
                allow-create
                size="large"
                :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                :placeholder="$t('rules.enterCreate')"
                @on-create="addField"
                @on-change="handleFieldsChange"
              >
                <template v-if="isDetail || accessType === 'edit' || isSelectConfiguration">
                  <Option v-for="(value, index) in record[property.name]" :value="value" :key="index">
                    {{ value }}
                  </Option>
                </template>
                <template v-else>
                  <Option v-for="value in filedData" :value="value" :key="value">
                    {{ value }}
                  </Option>
                </template>
              </Select>

              <!-- headers -->
              <template v-else-if="String(property.name).toLocaleLowerCase() === 'headers'">
                <span class="list-objet-mode">
                  <span class="text">{{ $t(`${JSONWayText}`) }}</span>
                  <i-switch v-model="header.mode" size="small" @on-change="changeHeaderMode(property.name)" />
                </span>
                <KeyValueEditor
                  ref="keyValueEditorRef"
                  v-if="!valueIsString(property.name)"
                  v-model="record[property.name]"
                  :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                  :properties="property.properties || property.default"
                  :submitDataType="property.type"
                />
                <KeyValueTextare v-else v-model="record[property.name]" :placeholder="header.placeholder" />
              </template>

              <KeyValueTableTreeEditor
                ref="keyValueTreeEditorRef"
                v-else-if="String(property.name).toLocaleLowerCase() === 'oauth'"
                v-model="record[property.name]"
                :properties="property.properties || property.default"
                :operable="false"
                :submitDataType="'object'"
              />
              <KeyValueEditor
                ref="keyValueEditorRef"
                v-else-if="['list_object', 'object'].includes(property.type)"
                v-model="record[property.name]"
                :templateData="property.templateData"
                :properties="property.properties || property.default"
                :modalVisible="modalVisible"
                :disabled="isDetail || (property.connection_related && isSelectConfiguration)"
                :submitDataType="property.type"
                :type="type"
              />
            </template>
          </FormItem>
        </i-col>
      </Row>
    </Form>
    <div v-if="showIndexFields" class="multi-index-title">
      <span>Multi Index Fields</span>
      <Tooltip
        max-width="250px"
        class="field-help"
        transfer
        placement="top"
        content="Supports configuring multiple index fields for query template"
      >
        <Icon type="md-help-circle" />
      </Tooltip>
    </div>
    <Tabs
      v-if="showIndexFields"
      class="multi-index"
      type="card"
      v-model="activeTab"
      :closable="indexFields.length > 1"
      :draggable="true"
      :before-remove="handleTabRemove"
      @on-drag-drop="handleDragDrop"
    >
      <TabPane v-for="item in indexFields" :key="item.name" :name="`${item.name}`" :label="`Index Field ${item.name}`">
        <Form :model="item" label-position="left" :label-width="200">
          <FormItem label="dateTimeFormat" prop="dateTimeFormat">
            <i-input v-model="item.dateTimeFormat" type="text" :disabled="isDetail" />
          </FormItem>
          <FormItem label="indexField" prop="indexField">
            <i-input v-model="item.indexField" type="text" :disabled="isDetail" />
          </FormItem>
          <FormItem label="indexFieldType" prop="indexFieldType">
            <Select
              v-model="item.indexFieldType"
              :disabled="isDetail"
              clearable
              @on-change="
                (value) => {
                  handleSelectChange(value, item.name)
                }
              "
            >
              <Option value="DATETIME">DATETIME</Option>
              <Option value="INT">INT</Option>
            </Select>
          </FormItem>
          <FormItem label="indexValue" prop="indexValue">
            <i-input v-if="item.indexFieldType === 'DATETIME'" v-model="item.indexValue" :disabled="isDetail" />
            <i-input
              v-else-if="item.indexFieldType === 'INT'"
              v-model.number="item.indexValue"
              type="number"
              :disabled="isDetail"
            />
          </FormItem>
        </Form>
      </TabPane>
      <template #extra>
        <Button @click="handleTabsAdd" size="small">Add</Button>
      </template>
    </Tabs>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash'
import KeyValueEditor from './KeyValueEditor.vue'
import KeyValueTextare from './KeyValueTextare.vue'
import nodeID from '@/mixins/nodeID'
import currentLang from '@/mixins/currentLang'
import { parseHeaderDataToArray } from '@/utils/handleData'
import KeyValueTableTreeEditor from '@/components/KeyValueTableTreeEditor/Index.vue'

export default {
  name: 'ActionsPropertyForm',

  components: {
    KeyValueEditor,
    KeyValueTextare,
    KeyValueTableTreeEditor,
  },

  mixins: [nodeID, currentLang],

  props: {
    category: { type: String, default: 'sinks' },
    isDetail: {
      type: Boolean,
      default: false,
    },
    properties: {
      requried: true,
      type: Array,
      default: () => [],
    },
    form: {
      type: Object,
      default: null,
    },
    type: {
      type: String,
      default: 'mqtt',
    },
    rule: {
      type: Boolean,
      default: false,
    },
    modalVisible: {
      type: Boolean,
      default: false,
    },
    // Is there a form of the same level that needs to be validate together
    sameLevelFormValid: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      record: {},
      accessType: this.$route.query.oper,
      filedData: [],
      isSelectConfiguration: false,
      header: {
        mode: true,
        placeholder: '{"key":"{{value}}"}',
      },
      indexFields: [
        {
          name: 1,
          dateTimeFormat: '',
          indexField: '',
          indexFieldType: '',
          indexValue: '',
        },
      ],
      activeTab: '1',
    }
  },

  computed: {
    valueIsString() {
      return (key) => {
        const res = typeof this.record[key] === 'string'
        this.header.mode = !res
        return res
      }
    },
    JSONWayText() {
      return this.header.mode ? 'rules.visualizationMode' : 'rules.textMode'
    },
    showIndexFields() {
      return this.category === 'sources' && this.type === 'sql'
    },
  },

  watch: {
    form: {
      handler(val) {
        this.record = val && Object.keys(val).length ? cloneDeep(val) : {}
        if (this.showIndexFields && val.templateSqlQueryCfg && val.templateSqlQueryCfg.indexFields) {
          const data = []
          val.templateSqlQueryCfg.indexFields.forEach((item, index) => {
            data.push({ ...item, name: index + 1 })
          })
          this.indexFields = data.length ? data : this.indexFields
          this.activeTab = '1'
        } else if (this.showIndexFields && val.templateSqlQueryCfgval && val.templateSqlQueryCfgval.indexField) {
          const { dateTimeFormat, indexField, indexFieldType, indexValue } = val.templateSqlQueryCfgval
          this.indexFields = [{ name: 1, dateTimeFormat, indexField, indexFieldType, indexValue }]
          this.activeTab = '1'
        }

        // handle list_object
        this.handlerProperties(this.properties)

        if (val?.servers) {
          val.servers.forEach((item) => {
            this.filedData.push(item)
          })
        }
        setTimeout(() => {
          this.$set(this.record, 'server', val.server)
          this.$set(this.record, 'port', val.port)
        })
      },
      deep: true,
      immediate: true,
    },
    properties(val) {
      if (val) {
        const item = val.find((res) => res.name === 'insecureSkipVerify')
        if (item && typeof item.defaul === 'boolean') {
          this.record.insecureSkipVerify = item.default
        }

        this.handlerProperties(val)
      }
    },
    record: {
      handler() {
        if (this.record.connectionSelector) {
          this.isSelectConfiguration = true
        } else {
          this.isSelectConfiguration = false
        }
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    // handle list_object in properties
    handlerProperties(properties) {
      const listObjectItem =
        // properties && properties.filter((res) => res.control === 'list' && res.type === 'list_object')
        properties &&
        properties.filter((res) => res.control === 'list' && (res.type === 'list_object' || res.type === 'object'))

      listObjectItem.forEach((item) => {
        const value = this.record[item.name] || this.record[item.name.toLocaleLowerCase()]

        if (!value && item.default) {
          const defalutValue =
            item.name.toLocaleLowerCase() === 'headers' ? parseHeaderDataToArray(item.default) : item.default
          this.record[item.name] = defalutValue
        }
      })
    },
    // change header input method
    changeHeaderMode(key) {
      this.record[key] = this.header.mode ? {} : ''
    },

    addField(val) {
      this.filedData.push(val)
    },
    handleFieldsChange(val) {
      if (val.length === 0) {
        val.push('')
      }
    },
    getFormData() {
      return new Promise((resolve, reject) => {
        this.$refs.record.validate((valid) => {
          if (!valid) {
            reject(new Error(this.$t('common.invalidateForm')))
          }
          if (this.showIndexFields) {
            const data = []
            this.indexFields.forEach((item) => {
              if (item.indexField) {
                data.push({
                  dateTimeFormat: item.dateTimeFormat,
                  indexField: item.indexField,
                  indexFieldType: item.indexFieldType,
                  indexValue: item.indexValue,
                })
              }
            })
            const templateSqlQueryCfg = { TemplateSql: this.record.templateSqlQueryCfg.TemplateSql, indexFields: data }
            this.record.templateSqlQueryCfg = templateSqlQueryCfg
          }
          resolve(this.record)
        })
      })
    },

    /* discard, connectionSeletor is generated on the front-end
    selectChange(val, name) {
      if (val && name === 'connectionSelector') {
        loadConnectionsDetail(this.nodeID, this.type).then((res) => {
          this.record = {}
          const sourceData = res.data
          const confKeyData = sourceData[val]
          confKeyData.connectionSelector = val
          if (this.rule) {
            confKeyData.clientId = confKeyData.clientid
            const propertieServer = this.properties.find((it) => it.name === 'server')
            if (propertieServer && propertieServer.type === 'string' && confKeyData.servers) {
              confKeyData.server = confKeyData.servers ? confKeyData.servers[0] : ''
            }
          }
          Object.keys(confKeyData).forEach((item) => {
            this.record[item] = confKeyData[item]
          })
          // this.$emit('fitName', val)
          this.isSelectConfiguration = true
        })
      }
    },
    */
    handleClear(name) {
      if (name === 'connectionSelector') {
        this.properties.forEach((item) => {
          if (item.connection_related) {
            this.$set(this.record, item.name, item.default)
          }
        })
      }
    },
    resetFields() {
      this.record = {}
    },

    handleDragDrop(name, newName, a, b) {
      this.indexFields.splice(b, 1, ...this.indexFields.splice(a, 1, this.indexFields[b]))
      this.indexFields.forEach((item, index) => {
        item.name = index + 1
      })
      this.activeTab = newName
    },

    handleTabRemove(tabIndex) {
      if (this.activeTab === String(this.indexFields[tabIndex].name)) {
        this.activeTab = '1'
      }
      this.indexFields.splice(tabIndex, 1)
      this.indexFields.forEach((item, index) => {
        item.name = index + 1
      })
      return Promise.reject()
    },

    handleTabsAdd() {
      this.indexFields.push({
        name: this.indexFields.length + 1,
        dateTimeFormat: '',
        indexField: '',
        indexFieldType: '',
        indexValue: '',
      })
      this.activeTab = String(this.indexFields.length)
    },

    // adapt sql source
    handleSelectChange(val, name) {
      this.indexFields.forEach((item) => {
        if (item.name === name) {
          item.indexValue = ''
        }
      })
    },

    // if you use `keyValueEditor` component, data transfer must be triggered, to avoid errors when validate with other forms
    async transferKeyValueEditorData() {
      const keyValueEditorRefs = this.$refs.keyValueEditorRef || []
      if (keyValueEditorRefs.length) {
        const transferDataList = []
        keyValueEditorRefs.forEach(async (item) => {
          transferDataList.push(item.transferData())
        })
        await Promise.allSettled(transferDataList)
      }
    },

    // if you use `keyValueTreeEditor` component, data transfer must be triggered, to avoid errors when validate with other forms
    async transferKeyValueTreeEditorData() {
      const keyValueTreeEditorRefs = this.$refs.keyValueTreeEditorRef || []
      if (keyValueTreeEditorRefs.length) {
        const transferDataList = []
        keyValueTreeEditorRefs.forEach(async (item) => {
          transferDataList.push(item.transferData())
        })
        await Promise.allSettled(transferDataList)
      }
    },

    async validatePropertiesForm() {
      try {
        const validators = [this.$refs.record.validate()]

        const keyValueEditorRefs = this.$refs.keyValueEditorRef || []
        const keyValueTreeEditorRefs = this.$refs.keyValueTreeEditorRef || []
        // keyValueEditor
        if (keyValueEditorRefs.length) {
          keyValueEditorRefs.forEach((item) => {
            const validator = item.validateKeyValueEditor
            validators.push(validator())
          })
        }
        // keyValueTreeEditor
        if (keyValueTreeEditorRefs.length) {
          keyValueTreeEditorRefs.forEach((item) => {
            const validator = item.validate
            validators.push(validator())
          })
        }

        const validRes = await Promise.allSettled(validators)
        const valid = validRes.every((item) => item?.value)

        if (valid) {
          if (this.sameLevelFormValid) {
            this.transferKeyValueEditorData()
            this.transferKeyValueTreeEditorData()
          }

          return Promise.resolve(valid)
        }

        this.$Message.error(this.$t('common.fillContent'))
        return Promise.reject(this.$t('common.fillContent'))
      } catch (error) {
        return Promise.reject(error)
      }
    },
  },
}
</script>

<style lang="scss">
.property-form {
  .list-objet-mode {
    float: right;
    .text {
      padding: 0 10px;
    }
  }
  .multi-index-title {
    margin-bottom: 10px;
  }
  .multi-index {
    .ivu-tabs-bar {
      margin-bottom: 10px;
      .ivu-tabs-nav-container {
        height: 40px !important;
      }
      .ivu-tabs-nav-right .ivu-btn {
        margin-top: 8px;
      }
    }
  }
}
</style>
